import { useAtomValue } from "jotai";
import { useMemo } from "react";
import styled from "styled-components";

import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import {
  labelOptionAtom,
  planogramImagesModeAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { resolvePlanogramProductLabel } from "../../common/planogramItemLabel";
import { projectPlanogramScene } from "../KonvaPlanogram/sceneProjection";
import type { CanvasItemLabelRule } from "../Item/Item";
import type { ShelfTagRule } from "../Shelf/MergedShelf";
import {
  CANVAS_LABEL_FONT_FAMILY,
  CANVAS_LABEL_LINE_HEIGHT,
  fitProductLabel,
  getCanvasLabelFontSize,
  type MeasureTextWidth,
} from "./labelFit";

/**
 * World-space layer for the Konva viewer: everything is in planogram units, and the canvas
 * scales it with the viewport, so labels grow and shrink with zoom like the products do.
 */
const AnnotationsLayer = styled.div<{ $fontSize: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 11;
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
`;

/** Laid out like the editor's canvas label: centred in the product box, black, Arial. */
const ItemLabel = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ItemLabelText = styled.div<{ $onImage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: ${CANVAS_LABEL_FONT_FAMILY};
  font-weight: bold;
  line-height: ${CANVAS_LABEL_LINE_HEIGHT};
  white-space: nowrap;
  color: #000000;
  ${({ $onImage }) =>
    $onImage && "background: rgba(255, 255, 255, 0.85); border-radius: 0.2em; padding: 0 0.2em;"}
`;

const ShelfTagAnchor = styled.div`
  position: absolute;
  transform: translateX(-100%);
  padding: 0.2em;
`;

/** Takes shelf items and pegboard products alike. */
export type CanvasProductCaptionRule = (product: BaseProduct) => string | null | undefined;

interface Props {
  pog: Pog;
  /** Shelf items only: the value drawn on each product. */
  canvasItemLabelRule?: CanvasItemLabelRule;
  /**
   * The line above the value on coloured blocks, on shelf items and pegboard products. When given
   * it replaces the editor's label option; an empty result leaves the value on its own.
   */
  canvasItemCaptionRule?: CanvasProductCaptionRule;
  shelfTagRule?: ShelfTagRule;
  /** Test seam; the page measures with a canvas context. */
  measureText?: MeasureTextWidth;
}

export const ViewerWorldAnnotations = ({
  pog,
  canvasItemLabelRule,
  canvasItemCaptionRule,
  shelfTagRule,
  measureText,
}: Props) => {
  const imagesMode = useAtomValue(planogramImagesModeAtom);
  const labelOption = useAtomValue(labelOptionAtom);
  const scene = useMemo(() => projectPlanogramScene(pog, "none"), [pog]);
  const canvasLabelFontSize = getCanvasLabelFontSize(scene.height);

  // The canvas leaves its own labels off while these rules are set, so every product it draws,
  // shelf item or pegboard placement, needs one here.
  const itemLabels = useMemo(() => {
    if (!canvasItemLabelRule && !canvasItemCaptionRule) return [];

    type Box = { id: string; x: number; y: number; width: number; height: number };
    const fitLabel = (key: string, box: Box, product: BaseProduct, value: string) => {
      // On coloured blocks a caption sits above the value: the page's own, or the editor's label.
      const productLabel = imagesMode
        ? ""
        : canvasItemCaptionRule
          ? (canvasItemCaptionRule(product) ?? "")
          : labelOption !== "none"
            ? resolvePlanogramProductLabel(product, labelOption, "")
            : "";
      const fitted = fitProductLabel({
        label: productLabel,
        value,
        width: box.width,
        height: box.height,
        maxFontSize: canvasLabelFontSize,
        measure: measureText,
      });

      return fitted.valueFontSize > 0 || fitted.labelLines.length > 0 ? [{ key, box, fitted }] : [];
    };

    const pegProducts = new Map<string, BaseProduct>(
      pog.planogram
        .getAllPegboardPlacements()
        .map((placement) => [placement.uniqueId, placement.pegProduct]),
    );

    return [
      ...scene.products.flatMap((product) => {
        const item = pog.getItem(product.id);
        if (!(item instanceof PlanogramItem)) return [];
        return fitLabel(`item-${product.id}`, product, item, canvasItemLabelRule?.(item) ?? "");
      }),
      ...(scene.placements ?? []).flatMap((placement) => {
        const pegProduct = pegProducts.get(placement.id);
        return pegProduct ? fitLabel(`placement-${placement.id}`, placement, pegProduct, "") : [];
      }),
    ];
  }, [
    canvasItemCaptionRule,
    canvasItemLabelRule,
    canvasLabelFontSize,
    imagesMode,
    labelOption,
    measureText,
    pog,
    scene,
  ]);

  const shelfTags = useMemo(() => {
    if (!shelfTagRule) return [];
    const groups = new Map<string, typeof scene.shelves>();
    for (const shelf of scene.shelves) {
      groups.set(shelf.groupId, [...(groups.get(shelf.groupId) ?? []), shelf]);
    }
    return [...groups.entries()].flatMap(([groupId, sceneShelves]) => {
      const shelves = sceneShelves
        .map((sceneShelf) => pog.getShelf(sceneShelf.id))
        .filter((shelf): shelf is PlanogramShelf => Boolean(shelf));
      if (shelves.length === 0 || shelves[0].type === "HANGCELL") return [];
      return [
        {
          groupId,
          right: Math.max(...sceneShelves.map((shelf) => shelf.x + shelf.width)),
          top: Math.min(...sceneShelves.map((shelf) => shelf.nonMerchandisableExtensionY)),
          content: shelfTagRule(shelves),
        },
      ];
    });
  }, [pog, scene, shelfTagRule]);

  if (itemLabels.length === 0 && shelfTags.length === 0) return null;

  return (
    <AnnotationsLayer $fontSize={canvasLabelFontSize} data-testid="viewer-world-annotations">
      {itemLabels.map(({ key, box, fitted }) => (
        <ItemLabel
          key={key}
          style={{
            left: box.x,
            top: box.y,
            width: box.width,
            height: box.height,
          }}
        >
          <ItemLabelText $onImage={imagesMode}>
            {fitted.labelLines.map((line, index) => (
              <span key={index} style={{ fontSize: fitted.labelFontSize, fontWeight: "normal" }}>
                {line}
              </span>
            ))}
            {fitted.valueFontSize > 0 && (
              <span data-label-value style={{ fontSize: fitted.valueFontSize }}>
                {fitted.value}
              </span>
            )}
          </ItemLabelText>
        </ItemLabel>
      ))}

      {shelfTags.map(({ groupId, right, top, content }) => (
        <ShelfTagAnchor key={groupId} style={{ left: right, top }}>
          {content}
        </ShelfTagAnchor>
      ))}
    </AnnotationsLayer>
  );
};
